"use client";
import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, ShieldCheck, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import type { ConfirmationResult } from "firebase/auth";

interface PhoneAuthModalProps {
    open: boolean;
    onClose: () => void;
}

const RECAPTCHA_ID = "recaptcha-container-phone-modal";
const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

// ─── 6-box autofocusing OTP input ──────────────────────────────────────────
const OtpInput = ({
    value,
    onChange,
}: {
    value: string;
    onChange: (v: string) => void;
}) => {
    const refs = useRef<(HTMLInputElement | null)[]>([]);
    const digits = value.padEnd(OTP_LENGTH, " ").split("").slice(0, OTP_LENGTH);

    const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace") {
            const next = digits.map((d, idx) => (idx === i ? " " : d));
            onChange(next.join("").trimEnd());
            if (i > 0) refs.current[i - 1]?.focus();
        }
    };

    const handleChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const char = e.target.value.replace(/\D/g, "").slice(-1);
        if (!char) return;
        const next = digits.map((d, idx) => (idx === i ? char : d));
        onChange(next.join("").trimEnd());
        if (i < OTP_LENGTH - 1) refs.current[i + 1]?.focus();
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        onChange(pasted);
        const nextIdx = Math.min(pasted.length, OTP_LENGTH - 1);
        refs.current[nextIdx]?.focus();
    };

    return (
        <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={(el) => { refs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d.trim()}
                    onChange={(e) => handleChange(i, e)}
                    onKeyDown={(e) => handleKey(i, e)}
                    autoFocus={i === 0}
                    aria-label={`OTP digit ${i + 1}`}
                    title={`OTP digit ${i + 1}`}
                    className="w-11 h-12 text-center text-lg font-bold border-2 rounded-xl
            border-border bg-background text-foreground
            focus:outline-none focus:border-primary transition-colors"
                />
            ))}
        </div>
    );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────
const PhoneAuthModal = ({ open, onClose }: PhoneAuthModalProps) => {
    const { sendOtp, verifyOtp } = useAuth();

    const [step, setStep] = useState<"phone" | "otp">("phone");
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [countdown, setCountdown] = useState(0);

    // Countdown timer
    useEffect(() => {
        if (countdown <= 0) return;
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [countdown]);

    // Reset state and clean up reCAPTCHA verifier when modal closes
    useEffect(() => {
        if (!open) {
            // Destroy the old RecaptchaVerifier so the next open starts fresh
            const win = window as Window & { __recaptchaVerifier?: { clear: () => void } };
            if (win.__recaptchaVerifier) {
                try { win.__recaptchaVerifier.clear(); } catch { /* ignore */ }
                win.__recaptchaVerifier = undefined;
            }
            // Clear the container DOM so Firebase won't complain "already rendered"
            const el = document.getElementById(RECAPTCHA_ID);
            if (el) el.innerHTML = "";

            setTimeout(() => {
                setStep("phone");
                setPhone("");
                setOtp("");
                setConfirmation(null);
                setCountdown(0);
            }, 300);
        }
    }, [open]);

    const handleSendOtp = useCallback(async () => {
        const trimmed = phone.trim();
        if (!/^\d{10}$/.test(trimmed)) {
            toast.error("Please enter a valid 10-digit mobile number");
            return;
        }
        setSending(true);
        try {
            const fullPhone = `+91${trimmed}`;
            const conf = await sendOtp(fullPhone, RECAPTCHA_ID);
            setConfirmation(conf);
            setStep("otp");
            setCountdown(RESEND_SECONDS);
            toast.success("OTP sent! Check your messages 📲");
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "Failed to send OTP";
            toast.error(msg);
        } finally {
            setSending(false);
        }
    }, [phone, sendOtp]);

    const handleVerify = useCallback(async () => {
        if (!confirmation) return;
        if (otp.length !== OTP_LENGTH) {
            toast.error("Enter the 6-digit OTP");
            return;
        }
        setVerifying(true);
        try {
            await verifyOtp(confirmation, otp);
            toast.success("Logged in successfully! 🎉");
            onClose();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "Invalid OTP";
            toast.error(msg);
        } finally {
            setVerifying(false);
        }
    }, [confirmation, otp, verifyOtp, onClose]);

    const handleResend = () => {
        setStep("phone");
        setOtp("");
    };

    return (
        <>
            {/*
        *** CRITICAL ***
        This div MUST be outside AnimatePresence / any conditional render so it
        is always in the DOM while PhoneAuthModal is mounted.

        Firebase's RecaptchaVerifier holds a reference to this element internally.
        If the element is removed and recreated (i.e., inside {open && ...}), the
        next call to signInWithPhoneNumber throws:
          "reCAPTCHA has already been rendered in this element"
          → auth/invalid-app-credential

        Keeping it here (hidden via CSS) solves both errors.
      */}
            <div id={RECAPTCHA_ID} className="hidden" />

            <AnimatePresence>
                {open && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            key="backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
                            onClick={onClose}
                        />

                        {/* Sheet */}
                        <motion.div
                            key="sheet"
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 28, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 z-50 bg-background rounded-t-3xl
                shadow-2xl px-6 pt-5 pb-10 max-w-md mx-auto"
                        >
                            {/* Handle */}
                            <div className="w-10 h-1 rounded-full bg-muted-foreground/30 mx-auto mb-5" />

                            {/* Close */}
                            <button
                                onClick={onClose}
                                title="Close"
                                aria-label="Close phone login modal"
                                className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center
                  rounded-full bg-muted hover:bg-muted/80 transition-colors"
                            >
                                <X className="w-4 h-4 text-muted-foreground" />
                            </button>

                            <AnimatePresence mode="wait">
                                {step === "phone" ? (
                                    <motion.div
                                        key="phone-step"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="space-y-6"
                                    >
                                        {/* Icon + title */}
                                        <div className="flex flex-col items-center gap-2 text-center">
                                            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                                                <Phone className="w-7 h-7 text-primary" />
                                            </div>
                                            <h2 className="text-lg font-bold text-foreground">Login with Mobile</h2>
                                            <p className="text-sm text-muted-foreground">
                                                We'll send a one-time OTP to your number
                                            </p>
                                        </div>

                                        {/* Phone input */}
                                        <div className="flex items-center border-2 border-border rounded-xl overflow-hidden
                      focus-within:border-primary transition-colors bg-background">
                                            <span className="px-4 py-3 text-sm font-semibold text-muted-foreground border-r border-border bg-muted/30">
                                                +91
                                            </span>
                                            <input
                                                type="tel"
                                                inputMode="numeric"
                                                placeholder="10-digit mobile number"
                                                maxLength={10}
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                                                onKeyDown={(e) => { if (e.key === "Enter") handleSendOtp(); }}
                                                className="flex-1 px-4 py-3 text-sm bg-transparent text-foreground
                          placeholder:text-muted-foreground focus:outline-none"
                                            />
                                        </div>

                                        <button
                                            onClick={handleSendOtp}
                                            disabled={sending}
                                            className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground
                        font-semibold text-sm disabled:opacity-60 transition-opacity
                        flex items-center justify-center gap-2"
                                        >
                                            {sending ? (
                                                <>
                                                    <span className="w-4 h-4 border-2 border-primary-foreground
                            border-t-transparent rounded-full animate-spin" />
                                                    Sending OTP…
                                                </>
                                            ) : (
                                                "Send OTP"
                                            )}
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="otp-step"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        className="space-y-6"
                                    >
                                        {/* Title */}
                                        <div className="flex flex-col items-center gap-2 text-center">
                                            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                                                <ShieldCheck className="w-7 h-7 text-primary" />
                                            </div>
                                            <h2 className="text-lg font-bold text-foreground">Enter OTP</h2>
                                            <p className="text-sm text-muted-foreground">
                                                Sent to <span className="font-medium text-foreground">+91 {phone}</span>
                                            </p>
                                        </div>

                                        {/* OTP boxes */}
                                        <OtpInput value={otp} onChange={setOtp} />

                                        <button
                                            onClick={handleVerify}
                                            disabled={verifying || otp.length !== OTP_LENGTH}
                                            className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground
                        font-semibold text-sm disabled:opacity-60 transition-opacity
                        flex items-center justify-center gap-2"
                                        >
                                            {verifying ? (
                                                <>
                                                    <span className="w-4 h-4 border-2 border-primary-foreground
                            border-t-transparent rounded-full animate-spin" />
                                                    Verifying…
                                                </>
                                            ) : (
                                                "Verify & Login"
                                            )}
                                        </button>

                                        {/* Resend */}
                                        <p className="text-center text-sm text-muted-foreground">
                                            {countdown > 0 ? (
                                                <>Resend OTP in <span className="font-medium text-foreground">{countdown}s</span></>
                                            ) : (
                                                <button
                                                    onClick={handleResend}
                                                    className="text-primary font-medium inline-flex items-center gap-1"
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                    Change number / Resend
                                                </button>
                                            )}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default PhoneAuthModal;
